import type { Policy } from '@prm/shared';
import { DEFAULT_POLICY } from '@prm/shared';
import { Panel } from './Panel';

type NumericKey = Exclude<keyof Policy, 'clusterThresholds'>;

interface Field {
  key: NumericKey;
  label: string;
  /** How the stored decimal is shown and typed. */
  unit: 'pct' | 'mult';
  help: string;
}

const FIELDS: Field[] = [
  { key: 'maxStressDrawdown', label: 'Max stress drawdown', unit: 'pct', help: 'Worst tolerable modelled loss in one scenario.' },
  { key: 'maxPositionWeight', label: 'Single-name cap', unit: 'pct', help: 'Largest position on economic notional.' },
  { key: 'maxSectorWeight', label: 'Sector cap', unit: 'pct', help: 'Largest sector.' },
  { key: 'maxGross', label: 'Gross cap', unit: 'mult', help: 'Economic notional over equity, counting leverage.' },
  { key: 'volTargetDaily', label: 'Vol target (daily)', unit: 'pct', help: 'Target daily portfolio volatility.' },
  { key: 'volTolerance', label: 'Vol tolerance', unit: 'mult', help: 'Multiple of target that counts as a breach.' },
  { key: 'maxClusterWeight', label: 'Cluster cap', unit: 'pct', help: 'Largest share of gross in one co-moving cluster.' },
  { key: 'maxTop3RiskShare', label: 'Top-3 risk cap', unit: 'pct', help: 'Largest share of variance in three names.' },
  { key: 'maxHedgeShare', label: 'Hedge-share cap', unit: 'pct', help: 'Largest share of gross held as hedges.' },
  { key: 'maxHedgeAnnualCost', label: 'Hedge cost cap /yr', unit: 'pct', help: 'Annualized option spend before "long vol donation".' },
];

/**
 * The policy object, editable. Every threshold in the app reads from here, so a
 * change propagates to flags, checks, gates and the decision verdict on the next
 * analyze — there is no second copy of any of these numbers.
 */
export function PolicyEditor({ policy, onChange }: { policy: Policy; onChange: (next: Policy) => void }) {
  const set = (key: NumericKey, raw: string, unit: Field['unit']) => {
    const n = Number(raw.replace(/[%×\s]/g, ''));
    if (!Number.isFinite(n)) return;
    onChange({ ...policy, [key]: unit === 'pct' ? n / 100 : n });
  };

  const show = (value: number, unit: Field['unit']) =>
    unit === 'pct' ? String(Number((value * 100).toFixed(4))) : String(value);

  return (
    <Panel
      title="Policy"
      sub="every limit the app enforces"
      actions={
        <button className="button button--sm" onClick={() => onChange(DEFAULT_POLICY)}>
          Reset defaults
        </button>
      }
    >
      <div className="policy-editor-grid">
        {FIELDS.map((f) => (
          <label className="policy-field" key={f.key} title={f.help}>
            <span className="lbl">{f.label}</span>
            <span className="policy-field__input">
              <input
                className="input input--num"
                value={show(policy[f.key], f.unit)}
                inputMode="decimal"
                onChange={(e) => set(f.key, e.target.value, f.unit)}
              />
              <span className="dim">{f.unit === 'pct' ? '%' : '×'}</span>
            </span>
          </label>
        ))}

        <label className="policy-field" title="Average pairwise correlation at which names count as one position.">
          <span className="lbl">Cluster hard</span>
          <span className="policy-field__input">
            <input
              className="input input--num"
              value={String(policy.clusterThresholds.hard)}
              inputMode="decimal"
              onChange={(e) =>
                onChange({
                  ...policy,
                  clusterThresholds: { ...policy.clusterThresholds, hard: Number(e.target.value) || 0 },
                })
              }
            />
            <span className="dim">ρ</span>
          </span>
        </label>

        <label className="policy-field" title="Average pairwise correlation at which names converge in risk-off.">
          <span className="lbl">Cluster soft</span>
          <span className="policy-field__input">
            <input
              className="input input--num"
              value={String(policy.clusterThresholds.soft)}
              inputMode="decimal"
              onChange={(e) =>
                onChange({
                  ...policy,
                  clusterThresholds: { ...policy.clusterThresholds, soft: Number(e.target.value) || 0 },
                })
              }
            />
            <span className="dim">ρ</span>
          </span>
        </label>
      </div>

      <div className="footnote">
        Saved with the portfolio. Changing a limit changes every flag, policy check, playbook gate and the decision
        verdict on the next analyze.
      </div>
    </Panel>
  );
}
